import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ClaimRequestOtpResponse } from '@astrologermatch/shared';
import { SITE_NAME } from '../components/Layout.js';
import { api, ApiError } from '../lib/api.js';
import { useSeo } from '../lib/seo.js';

// "Request removal" (batch: unclaimed profiles §5): verify ownership with an OTP to the email on
// file, or — if that's no longer reachable — leave a note for admin review.

type Step = 'intro' | 'code' | 'manual' | 'done';

export function RemoveProfile({
  data,
}: {
  data: { slug: string; name: string; claimed: boolean };
}) {
  useSeo({ title: `Request removal | ${SITE_NAME}`, robots: 'noindex' });

  const [step, setStep] = useState<Step>('intro');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [doneMessage, setDoneMessage] = useState('');

  if (data.claimed) {
    return (
      <div className="wrap page-pad">
        <div className="card narrow center-card">
          <h1 className="h2">This profile has been claimed</h1>
          <p className="muted">
            Removal requests are only for unclaimed profiles. If this is your account, sign in and
            use Account settings instead.
          </p>
          <Link to={`/astrologers/${data.slug}`} className="btn btn-primary">
            View profile
          </Link>
        </div>
      </div>
    );
  }

  async function requestOtp() {
    setBusy(true);
    setErr('');
    try {
      const res = await api<ClaimRequestOtpResponse>(
        'POST',
        `/api/v1/astrologers/${data.slug}/removal/request-otp`,
      );
      setMaskedEmail(res.maskedEmail);
      setStep('code');
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const res = await api<{ message: string }>(
        'POST',
        `/api/v1/astrologers/${data.slug}/removal/verify-otp`,
        { code: code.trim() },
      );
      setDoneMessage(res.message);
      setStep('done');
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function submitManual(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const res = await api<{ message: string }>(
        'POST',
        `/api/v1/astrologers/${data.slug}/removal/request-manual`,
        { note: note.trim() || undefined },
      );
      setDoneMessage(res.message);
      setStep('done');
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="wrap page-pad">
      <section className="card narrow center-card auth-card">
        <h1 className="h2">Request removal</h1>
        <p className="muted">
          {data.name}&apos;s profile was created by AstrologerMatch from public information. If it
          isn&apos;t you, or you&apos;d rather it wasn&apos;t listed, you can ask us to remove it.
        </p>
        {err && (
          <div className="alert alert-error" role="alert">
            {err}
          </div>
        )}

        {step === 'intro' && (
          <>
            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={busy}
              onClick={() => void requestOtp()}
            >
              {busy ? 'Sending…' : 'Verify with a code to the email on file'}
            </button>
            <p className="tiny muted mt16">
              Don&apos;t have access to that email?{' '}
              <button
                type="button"
                className="linkish"
                onClick={() => {
                  setStep('manual');
                }}
              >
                Ask us to review it manually
              </button>
            </p>
          </>
        )}

        {step === 'code' && (
          <form onSubmit={(e) => void verifyOtp(e)}>
            <p className="small muted">
              We sent a 6-digit code to <b>{maskedEmail}</b>.
            </p>
            <label>
              Verification code
              <input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, ''));
                }}
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={busy || code.length !== 6}
            >
              {busy ? 'Verifying…' : 'Verify and request removal'}
            </button>
          </form>
        )}

        {step === 'manual' && (
          <form onSubmit={(e) => void submitManual(e)}>
            <label>
              Tell us a bit more (optional)
              <textarea
                rows={4}
                maxLength={2000}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                placeholder="e.g. this isn't me, or I no longer practise astrology"
              />
            </label>
            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? 'Sending…' : 'Request removal'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="alert alert-info" role="status">
            {doneMessage}
          </div>
        )}
      </section>
    </div>
  );
}
